import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronDown, 
  Phone, 
  Mail, 
  Search,
  Code,
  Shield,
  Megaphone,
  Cloud,
  Cpu,
  BarChart,
  Database,
  Users,
  Briefcase,
  Home as HomeIcon,
  MessageSquare,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube
} from 'lucide-react';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle mouse enter with delay
  const handleMouseEnter = (index) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(index);
  };

  // Handle mouse leave with delay
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  // Navigation items with proper routes
  const navItems = [
    { 
      label: 'Home', 
      path: '/',
      dropdown: false,
      icon: HomeIcon
    },
    { 
      label: 'About Us', 
      path: '/about',
      dropdown: false,
      icon: Users
    },
    { 
      label: 'Services', 
      path: '#',
      dropdown: true,
      icon: Briefcase,
      items: [
        { 
          icon: Code, 
          label: 'Web Development', 
          description: 'Custom web applications & solutions',
          path: '/web-development'
        },
        { 
          icon: Megaphone, 
          label: 'Digital Marketing', 
          description: 'Data-driven marketing strategies',
          path: '/digital-marketing'
        },
        { 
          icon: Shield, 
          label: 'Cybersecurity', 
          description: 'Advanced security solutions',
          path: '/cybersecurity'
        },
        
      ]
    },
    { 
      label: 'Portfolio', 
      path: '/portfolio',
      dropdown: false,
      icon: BarChart
    },
    
    { 
      label: 'Team',  
      path: '/team',
      dropdown: false,
      icon: Users
    },
    { 
      label: 'Contact Us', 
      path: '/contact',
      dropdown: false,
      icon: MessageSquare
    },
  ];

  // Social media icons
  const socialIcons = [
    { icon: Facebook, label: 'Facebook', url: '#' },
    { icon: Twitter, label: 'Twitter', url: '#' },
    { icon: Linkedin, label: 'LinkedIn', url: '#' },
    { icon: Instagram, label: 'Instagram', url: '#' },
    { icon: Youtube, label: 'YouTube', url: '#' },
  ];

  return (
    <>
      
     

      {/* Main Navigation */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' : 'bg-white'}`}>
       <div className="container mx-auto px-6 sm:px-8 lg:px-16">

          <nav className="flex items-center justify-between py-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3">
 <img
  src="/KANDAX_Canada_Logo.png"
  alt="KANDAX Logo"
  className="h-14 w-auto object-contain"
 />

  <div>
     <h1 className="text-2xl font-bold text-gray-900">
      KANDAX
    </h1>
    <p className="text-xs text-red-600 -mt-1 font-medium">
      Human-Led Technology
    </p>
  </div>
</Link>

            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <div
                  key={index}
                  className="relative"
                  ref={item.dropdown ? dropdownRef : null}
                  onMouseEnter={() => item.dropdown && handleMouseEnter(index)}
                  onMouseLeave={item.dropdown ? handleMouseLeave : undefined}
                >
                  {item.dropdown ? (
                    <>
                      <button
                        className={`flex items-center space-x-1 transition-colors duration-200 font-medium cursor-pointer ${location.pathname === item.path ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
                        onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {activeDropdown === index && (
                        <div 
                          className="absolute left-0 mt-2 w-80 bg-white border border-red-200 rounded-xl shadow-2xl overflow-hidden z-50"
                          onMouseEnter={() => handleMouseEnter(index)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {item.items && item.items.map((dropdownItem, dIndex) => (
                            <Link
                              key={dIndex}
                              to={dropdownItem.path}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-red-50 border-b border-gray-100 last:border-b-0 group/dropdown transition-colors duration-200"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {dropdownItem.icon && (
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-100 to-red-50 border border-red-200 flex items-center justify-center transition-all duration-200 group-hover/dropdown:border-red-300">
                                  <dropdownItem.icon className="w-5 h-5 text-red-600 transition-all duration-200 group-hover/dropdown:scale-110" />
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-gray-900 group-hover/dropdown:text-red-600 transition-colors duration-200">
                                  {dropdownItem.label}
                                </div>
                                {dropdownItem.description && (
                                  <div className="text-xs text-gray-500 group-hover/dropdown:text-gray-600 transition-colors duration-200">
                                    {dropdownItem.description}
                                  </div>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-1 transition-colors duration-200 font-medium ${location.pathname === item.path ? 'text-red-600' : 'text-gray-700 hover:text-red-600'}`}
                    >
                      <span>{item.label}</span>
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <a 
                href="tel:+15551234567"
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border-2 border-red-600 text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                <Phone className="w-4 h-4" />
                <span>+1 (416) 700-7091</span>
              </a>
              <Link 
                to="/contact"
                className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white transition-all duration-300 hover:scale-105"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md border-t border-red-200 shadow-2xl transition-all duration-300 z-50 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col space-y-4">
              {navItems.map((item, index) => (
                <div key={index}>
                  {item.dropdown ? (
                    <details className="group">
                      <summary className="flex items-center justify-between py-3 text-gray-700 hover:text-red-600 cursor-pointer list-none transition-colors duration-200">
                        <div className="flex items-center space-x-2">
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                        </div>
                        <ChevronDown className="w-5 h-5 transition-transform group-open:rotate-180 duration-200" />
                      </summary>
                      <div className="ml-8 mt-2 space-y-2 border-l border-red-200 pl-4">
                        {item.items && item.items.map((dropdownItem, dIndex) => (
                          <Link
                            key={dIndex}
                            to={dropdownItem.path}
                            className="flex items-center space-x-3 py-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                              <dropdownItem.icon className="w-4 h-4 text-red-600" />
                            </div>
                            <div>
                              <div className="font-medium">{dropdownItem.label}</div>
                              <div className="text-xs text-gray-500">{dropdownItem.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <Link
                      to={item.path}
                      className="flex items-center space-x-2 py-3 text-gray-700 hover:text-red-600 font-medium transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile Action Buttons */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <a 
                    href="tel:+15551234567"
                    className="flex-1 flex items-center justify-center space-x-2 py-3 rounded-lg border-2 border-red-600 text-red-600 hover:bg-red-50 transition-colors duration-200"
                  >
                    <Phone className="w-5 h-5" />
                    <span>+1 (416) 700-7091</span>
                   
                  </a>
                  <Link 
                    to="/contact"
                    className="flex-1 ml-4 py-3 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white font-bold text-center hover:from-red-700 hover:to-red-800 transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
                
                
                
              
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="h-1 w-full bg-gradient-to-r from-red-600 via-red-400 to-red-600"></div>
      </header>
    </>
  );
}

export default Header;